import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Zap,
  Compass,
  GitBranch,
  Filter,
  Building2,
  CheckCircle2,
  Trash2,
  Copy,
  Sparkles,
  UserPlus,
  PieChart,
  Shuffle,
} from 'lucide-react';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';

const iconMap = {
  Zap,
  Compass,
  GitBranch,
  Filter,
  Building2,
  CheckCircle2,
  Sparkles,
  UserPlus,
  PieChart,
  Shuffle,
};

const getNodeTheme = (type) => {
  switch (type) {
    case 'start':
      return {
        color: '#F59E0B',
        badgeBg: 'rgba(245, 158, 11, 0.15)',
        badgeColor: '#FBBF24',
        border: 'rgba(245, 158, 11, 0.4)',
        label: 'START',
      };
    case 'route':
      return {
        color: '#3B82F6',
        badgeBg: 'rgba(59, 130, 246, 0.15)',
        badgeColor: '#60A5FA',
        border: 'rgba(59, 130, 246, 0.4)',
        label: 'ROUTE ENGINE',
      };
    case 'condition':
      return {
        color: '#A855F7',
        badgeBg: 'rgba(168, 85, 247, 0.15)',
        badgeColor: '#C084FC',
        border: 'rgba(168, 85, 247, 0.4)',
        label: 'CONDITION (IF/ELSE)',
      };
    case 'probability':
      return {
        color: '#EC4899',
        badgeBg: 'rgba(236, 72, 153, 0.15)',
        badgeColor: '#F472B6',
        border: 'rgba(236, 72, 153, 0.4)',
        label: 'PROBABILITY SPLIT',
      };
    case 'end':
      return {
        color: '#64748B',
        badgeBg: 'rgba(100, 116, 139, 0.15)',
        badgeColor: '#94A3B8',
        border: 'rgba(100, 116, 139, 0.4)',
        label: 'END',
      };
    case 'offerwall':
      return {
        color: '#10B981',
        badgeBg: 'rgba(16, 185, 129, 0.15)',
        badgeColor: '#34D399',
        border: 'rgba(16, 185, 129, 0.4)',
        label: 'OFFERWALL REDIRECT',
      };
    default:

      return {
        color: '#64748B',
        badgeBg: 'rgba(100, 116, 139, 0.15)',
        badgeColor: '#94A3B8',
        border: 'rgba(100, 116, 139, 0.4)',
        label: 'NODE',
      };
  }
};

const BaseNode = memo(({ id, type, data = {}, selected }) => {
  const theme = getNodeTheme(type);
  const IconComponent = iconMap[data.icon] || (type === 'start' ? Zap : type === 'condition' ? GitBranch : type === 'route' ? Compass : CheckCircle2);
  
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const duplicateNode = useWorkflowStore((state) => state.duplicateNode);
  const setSelectedNodeId = useWorkflowStore((state) => state.setSelectedNodeId);

  const isCondition = type === 'condition';
  const cases = data?.cases || [];

  const isProbability = type === 'probability';
  const branches = data?.branches || [];

  return (
    <div
      className="flow-custom-node"
      onClick={() => setSelectedNodeId(id)}
      style={{
        width: 270,
        background: 'var(--bg-surface)',
        borderRadius: 12,
        border: selected
          ? `2px solid ${theme.color}`
          : `1px solid ${theme.border}`,
        boxShadow: selected
          ? `0 0 0 4px ${theme.badgeBg}, var(--shadow-lg)`
          : 'var(--shadow)',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      {/* Target Input Handle (Not on start nodes) */}
      {type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            width: 12,
            height: 12,
            background: theme.color,
            border: '2px solid var(--bg-surface)',
            boxShadow: `0 0 8px ${theme.color}`,
            top: -6,
          }}
        />
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          background: 'rgba(0, 0, 0, 0.02)',
          borderTopLeftRadius: 11,
          borderTopRightRadius: 11,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: theme.badgeBg,
              color: theme.badgeColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent size={16} />
          </div>
          <div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: theme.badgeColor,
                textTransform: 'uppercase',
                display: 'block',
                lineHeight: 1,
              }}
            >
              {theme.label}
            </span>
          </div>
        </div>

        {/* Action icons */}
        {type !== 'start' && (
          <div className="node-actions" style={{ display: 'flex', gap: 4 }}>
            <button
              className="btn btn-ghost btn-icon"
              style={{ width: 22, height: 22, padding: 0 }}
              title="Duplicate"
              onClick={(e) => {
                e.stopPropagation();
                duplicateNode(id);
              }}
            >
              <Copy size={12} />
            </button>
            <button
              className="btn btn-ghost btn-icon"
              style={{ width: 22, height: 22, padding: 0, color: 'var(--red)' }}
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                deleteNode(id);
              }}
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        <h4
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: '0 0 4px 0',
            lineHeight: 1.3,
          }}
        >
          {data.label || data.title || (type === 'start' ? 'Start' : type === 'route' ? 'Route' : type === 'condition' ? 'Condition' : 'End')}
        </h4>

        <p
          style={{
            fontSize: 11,
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.4,
          }}
        >
          {data.subtitle || data.description || 'Click to configure node'}
        </p>

        {/* Route Details */}
        {type === 'route' && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 6,
                background: 'var(--blue-bg)',
                color: 'var(--blue)',
                fontWeight: 600,
              }}
            >
              Engine: {data.engine || 'waterfall'}
            </span>
            <span
              style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 6,
                background: 'var(--bg-raised)',
                color: 'var(--text-secondary)',
              }}
            >
              Buyer Set #{data.buyer_set_id || 1}
            </span>
          </div>
        )}

        {/* Condition Details */}
        {isCondition && (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontFamily: 'monospace',
                padding: '3px 6px',
                borderRadius: 6,
                background: 'var(--purple-bg)',
                color: 'var(--purple)',
                fontWeight: 600,
                display: 'inline-block',
              }}
            >
              Field: {data.field || 'score'}
            </div>
          </div>
        )}

        {/* Offerwall Details */}
        {type === 'offerwall' && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{
                fontSize: 10,
                fontFamily: 'monospace',
                padding: '3px 6px',
                borderRadius: 6,
                background: 'rgba(16, 185, 129, 0.15)',
                color: '#34D399',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              URL: {data.redirect_url || 'https://...'}
            </div>
            {data.payout ? (
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                Est. Payout: <strong>${data.payout}</strong>
              </span>
            ) : null}
          </div>
        )}
      </div>


      {/* Standard Outgoing Handle for start & route */}
      {(type === 'start' || type === 'route') && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            width: 12,
            height: 12,
            background: theme.color,
            border: '2px solid var(--bg-surface)',
            boxShadow: `0 0 8px ${theme.color}`,
            bottom: -6,
          }}
        />
      )}

      {/* Condition Node Outputs Footer */}
      {isCondition && (
        <div>
          {/* Branch list inside card footer */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              background: 'rgba(0, 0, 0, 0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              padding: '8px 10px',
              borderBottomLeftRadius: 11,
              borderBottomRightRadius: 11,
            }}
          >
            {cases.map((c, i) => (
              <div
                key={i}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--green)',
                  textAlign: 'center',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                }}
                title={c.case_id}
              >
                {c.case_id}
              </div>
            ))}
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--red)',
                textAlign: 'center',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                padding: '0 4px',
              }}
              title="false (else)"
            >
              else
            </div>
          </div>

          {/* Clean handle dots aligned with columns */}
          {cases.map((c, i) => {
            const count = cases.length + 1;
            const pct = Math.round(((i + 1) / (count + 1)) * 100);
            return (
              <Handle
                key={`${c.case_id || 'case'}-${i}`}
                type="source"
                position={Position.Bottom}
                id={c.case_id}
                title={`Branch: ${c.case_id}`}
                style={{
                  width: 12,
                  height: 12,
                  left: `${pct}%`,
                  bottom: -6,
                  background: 'var(--green)',
                  border: '2px solid var(--bg-surface)',
                  boxShadow: '0 0 8px var(--green)',
                }}
              />
            );
          })}

          {/* Mandatory Else / False Handle */}
          {(() => {
            const falsePct = Math.round(((cases.length + 1) / (cases.length + 2)) * 100);
            return (
              <Handle
                type="source"
                position={Position.Bottom}
                id="false"
                title="Branch: false (else)"
                style={{
                  width: 12,
                  height: 12,
                  left: `${falsePct}%`,
                  bottom: -6,
                  background: 'var(--red)',
                  border: '2px solid var(--bg-surface)',
                  boxShadow: '0 0 8px var(--red)',
                }}
              />
            );
          })()}
        </div>
      )}

      {/* Probability Node Outputs Footer */}
      {isProbability && (
        <div>
          {/* Branch list inside card footer */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              background: 'rgba(0, 0, 0, 0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              padding: '8px 10px',
              borderBottomLeftRadius: 11,
              borderBottomRightRadius: 11,
            }}
          >
            {branches.map((b, i) => {
              const rawLabel = b.label || b.branch_id || '';
              const cleanLabel = rawLabel.replace(/\s*\(\d+%\)$/, '');
              return (
                <div
                  key={i}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#F472B6',
                    textAlign: 'center',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    padding: '0 4px',
                  }}
                  title={`${cleanLabel} (${b.weight || 0}%)`}
                >
                  {cleanLabel} <span style={{ opacity: 0.85 }}>({b.weight || 0}%)</span>
                </div>
              );
            })}
          </div>

          {/* Clean handle dots aligned with columns */}
          {branches.map((b, i) => {
            const count = branches.length;
            const pct = Math.round(((i + 1) / (count + 1)) * 100);
            const rawLabel = b.label || b.branch_id || '';
            const cleanLabel = rawLabel.replace(/\s*\(\d+%\)$/, '');
            return (
              <Handle
                key={`${b.branch_id || 'branch'}-${i}`}
                type="source"
                position={Position.Bottom}
                id={b.branch_id}
                title={`${cleanLabel} (${b.weight || 0}%)`}
                style={{
                  width: 12,
                  height: 12,
                  left: `${pct}%`,
                  bottom: -6,
                  background: '#EC4899',
                  border: '2px solid var(--bg-surface)',
                  boxShadow: '0 0 8px #EC4899',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

export const nodeTypes = {
  start: (props) => <BaseNode {...props} type="start" />,
  route: (props) => <BaseNode {...props} type="route" />,
  condition: (props) => <BaseNode {...props} type="condition" />,
  probability: (props) => <BaseNode {...props} type="probability" />,
  offerwall: (props) => <BaseNode {...props} type="offerwall" />,
  end: (props) => <BaseNode {...props} type="end" />,
};

